import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {LoadingButton} from '@mui/lab';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import {useFormik} from 'formik';
import {divIcon, LeafletMouseEvent, Marker as LeafletMarker} from 'leaflet';
import {kebabCase} from 'lodash';
import {useSnackbar} from 'notistack';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
  ImageOverlay,
  MapContainer,
  Marker,
  Rectangle,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import * as yup from 'yup';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {MAP_MAX_ZOOM} from '../../constants/common';
import {useConfiguration} from '../../hooks/configuration';
import {useChunkedUpload} from '../../hooks/useChunkedUpload';
import {useMapSourcePreview} from '../../hooks/useMapSourcePreview';
import {
  canPreviewInBrowser,
  readImageHeaderSize,
} from '../../utils/imageHeader';
import {
  cornerLatLng,
  fieldsToBounds,
  moveBoundsBy,
  OverlayBounds,
  OverlayCorner,
  resizeBounds,
  shiftBounds,
  ShiftDirection,
  stepFromZoom,
  toLeafletBounds,
} from '../../utils/mapOverlay';
import {CloseSnackbarAction} from '../common/CloseSnackbarButton';
import SnackbarMessages from '../common/SnackbarMessages';

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'tif', 'tiff'];
const CORNERS: OverlayCorner[] = ['nw', 'ne', 'se', 'sw'];
const DEFAULT_CENTER: [number, number] = [38.5, -82.5];
const DEFAULT_ZOOM = 12;

const handleIcon = divIcon({
  className: '',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
  html: '<div style="width:12px;height:12px;border:2px solid #1976d2;background:#fff;border-radius:2px"></div>',
});

const getExtension = (fileName: string) =>
  (fileName.split('.').pop() ?? '').toLowerCase();

type MapSourceFormikValue = {
  file: File | null;
  name: string | null;
  path: string | null;
  north: number | null;
  south: number | null;
  east: number | null;
  west: number | null;
};

type MapViewControllerProps = {
  bounds: OverlayBounds | null;
  fitToken: number;
  onZoomChange: (zoom: number) => void;
};

const MapViewController: React.FC<MapViewControllerProps> = ({
  bounds,
  fitToken,
  onZoomChange,
}) => {
  const map = useMap();

  useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom());
    },
  });

  useEffect(() => {
    if (fitToken > 0 && bounds) {
      map.fitBounds(toLeafletBounds(bounds));
    }
  }, [fitToken]);

  return null;
};

type OverlayEditorProps = {
  bounds: OverlayBounds;
  imageUrl: string;
  opacity: number;
  onChange: (bounds: OverlayBounds) => void;
};

const OverlayEditor: React.FC<OverlayEditorProps> = ({
  bounds,
  imageUrl,
  opacity,
  onChange,
}) => {
  const map = useMap();
  const [dragOrigin, setDragOrigin] = useState<[number, number] | null>(null);

  useMapEvents({
    mousemove: (event: LeafletMouseEvent) => {
      if (!dragOrigin) {
        return;
      }

      onChange(
        moveBoundsBy(
          bounds,
          event.latlng.lat - dragOrigin[0],
          event.latlng.lng - dragOrigin[1]
        )
      );
      setDragOrigin([event.latlng.lat, event.latlng.lng]);
    },
    mouseup: () => {
      if (dragOrigin) {
        setDragOrigin(null);
        map.dragging.enable();
      }
    },
  });

  return (
    <>
      <ImageOverlay
        url={imageUrl}
        bounds={toLeafletBounds(bounds)}
        opacity={opacity}
      />

      <Rectangle
        bounds={toLeafletBounds(bounds)}
        pathOptions={{color: '#1976d2', weight: 1, fillOpacity: 0}}
        eventHandlers={{
          mousedown: (event: LeafletMouseEvent) => {
            map.dragging.disable();
            setDragOrigin([event.latlng.lat, event.latlng.lng]);
          },
        }}
      />

      {CORNERS.map((corner) => (
        <Marker
          key={corner}
          position={cornerLatLng(bounds, corner)}
          icon={handleIcon}
          draggable
          eventHandlers={{
            drag: (event) => {
              const position = (event.target as LeafletMarker).getLatLng();
              onChange(
                resizeBounds(bounds, corner, position.lat, position.lng)
              );
            },
          }}
        />
      ))}
    </>
  );
};

interface MapSourceUploadProps {
  onCancel?: Function;
  onSubmitted?: () => void;
}

export const MapSourceUpload: React.FC<MapSourceUploadProps> = ({
  onCancel,
  onSubmitted,
}) => {
  const {enqueueSnackbar} = useSnackbar();

  const configuredOpacity =
    Number(useConfiguration('global', 'map_opacity')?.value) / 100 || 0.7;
  const [opacity, setOpacity] = useState(configuredOpacity);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [fitToken, setFitToken] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [sourceFilename, setSourceFilename] = useState<string | null>(null);
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const sourceFilenameRef = useRef<string | null>(null);

  const serverPreview = useMapSourcePreview(sourceFilename);

  const inputValidationSchema = useMemo(
    () =>
      yup.object().shape({
        file: yup
          .mixed()
          .nullable()
          .required('Field is required')
          .test(
            'extension',
            `File must be one of: ${ALLOWED_EXTENSIONS.join(', ')}`,
            (value) =>
              !value ||
              ALLOWED_EXTENSIONS.includes(getExtension((value as File).name))
          ),
        name: yup.string().nullable().required('Field is required'),
        path: yup.string().nullable().required('Field is required'),
        north: yup
          .number()
          .nullable()
          .required('Field is required')
          .min(-90)
          .max(90),
        south: yup
          .number()
          .nullable()
          .required('Field is required')
          .min(-90)
          .max(90)
          .test(
            'south-below-north',
            'South must be less than North',
            (value, context) =>
              value === null ||
              value === undefined ||
              value < context.parent.north
          ),
        east: yup
          .number()
          .nullable()
          .required('Field is required')
          .min(-180)
          .max(180),
        west: yup
          .number()
          .nullable()
          .required('Field is required')
          .min(-180)
          .max(180)
          .test(
            'west-below-east',
            'West must be less than East',
            (value, context) =>
              value === null ||
              value === undefined ||
              value < context.parent.east
          ),
      }),
    []
  );

  const formik = useFormik<MapSourceFormikValue>({
    initialValues: {
      file: null,
      name: null,
      path: null,
      north: null,
      south: null,
      east: null,
      west: null,
    },
    validationSchema: inputValidationSchema,
    onSubmit: async () => {
      if (sourceFilename) {
        await createTilingJob();

        return;
      }

      if (formik.values.file) {
        upload.start(formik.values.file);
      }
    },
  });

  const imageUrl = localPreviewUrl ?? serverPreview.url;

  useEffect(
    () => () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    },
    [localPreviewUrl]
  );

  useEffect(
    () => () => {
      const abandoned = sourceFilenameRef.current;

      if (abandoned) {
        API.delete(`/map/source/${abandoned}`).catch(() => undefined);
      }
    },
    []
  );

  const bounds = useMemo(
    () =>
      fieldsToBounds({
        north: formik.values.north,
        south: formik.values.south,
        east: formik.values.east,
        west: formik.values.west,
      }),
    [
      formik.values.north,
      formik.values.south,
      formik.values.east,
      formik.values.west,
    ]
  );

  const applyBounds = (next: OverlayBounds) => {
    formik.setFieldValue('north', next.north);
    formik.setFieldValue('south', next.south);
    formik.setFieldValue('east', next.east);
    formik.setFieldValue('west', next.west);
  };

  const shift = (direction: ShiftDirection) => {
    if (!bounds) {
      return;
    }

    applyBounds(shiftBounds(bounds, direction, stepFromZoom(zoom)));
  };

  const upload = useChunkedUpload({
    url: `${apiBaseUrl}/map/source`,
    params: {
      name: formik.values.name,
      path: formik.values.path,
      sourceFileName: formik.values.file?.name,
      north: formik.values.north,
      south: formik.values.south,
      east: formik.values.east,
      west: formik.values.west,
    },
    onComplete: () => {
      enqueueSnackbar('Map queued for tiling — follow it in the map list.', {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      onSubmitted?.();
    },
    onError: (error) => {
      const messages = getMessagesFromApiError(error, 'STRA_4f21c8d0');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    },
  });

  const sourceUpload = useChunkedUpload({
    url: `${apiBaseUrl}/map/source/preview`,
    params: {sourceFileName: selectedFile?.name},
    onComplete: (response) => {
      const filename = (response as {sourceFilename?: string})?.sourceFilename;

      if (filename) {
        sourceFilenameRef.current = filename;
        setSourceFilename(filename);
      }
    },
    onError: (error) => {
      const messages = getMessagesFromApiError(error, 'STRA_4f21c8d1');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    },
  });

  const discardSource = () => {
    const abandoned = sourceFilenameRef.current;

    if (abandoned) {
      API.delete(`/map/source/${abandoned}`).catch(() => undefined);
    }

    sourceFilenameRef.current = null;
    setSourceFilename(null);
  };

  const selectFile = async (file: File) => {
    discardSource();
    setSelectedFile(file);
    setLocalPreviewUrl(null);
    formik.setFieldValue('file', file);

    if (!ALLOWED_EXTENSIONS.includes(getExtension(file.name))) {
      return;
    }

    const size = await readImageHeaderSize(file);

    if (canPreviewInBrowser(file.name, size)) {
      setLocalPreviewUrl(URL.createObjectURL(file));

      return;
    }

    setLocalPreviewUrl(null);
    sourceUpload.start(file);
  };

  const clearFile = () => {
    discardSource();
    sourceUpload.cancel();
    setLocalPreviewUrl(null);
    setSelectedFile(null);
    formik.setFieldValue('file', null);
  };

  const createTilingJob = async () => {
    setIsCreatingJob(true);

    try {
      await API.post('/map/tiling-job', null, {
        params: {
          name: formik.values.name,
          path: formik.values.path,
          sourceFilename,
          north: formik.values.north,
          south: formik.values.south,
          east: formik.values.east,
          west: formik.values.west,
        },
      });

      sourceFilenameRef.current = null;
      enqueueSnackbar('Map queued for tiling — follow it in the map list.', {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      onSubmitted?.();
    } catch (error) {
      const messages = getMessagesFromApiError(error, 'STRA_4f21c8d2');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    } finally {
      setIsCreatingJob(false);
    }
  };

  const isBusy =
    upload.isSubmitting || sourceUpload.isSubmitting || isCreatingJob;

  const coordinateField = (
    field: 'north' | 'south' | 'east' | 'west',
    label: string
  ) => (
    <TextField
      value={formik.values[field] ?? ''}
      fullWidth
      type="number"
      name={field}
      label={label}
      size="small"
      disabled={isBusy}
      error={!!formik.touched[field] && !!formik.errors[field]}
      helperText={formik.touched[field] && formik.errors[field]}
      onChange={(event) => {
        const value = event.target.value;
        formik.setFieldValue(field, value === '' ? null : Number(value));
        setFitToken((current) => current + 1);
      }}
    />
  );

  return (
    <Box
      component="form"
      display="flex"
      flexDirection="column"
      gap={3}
      onSubmit={formik.handleSubmit}
    >
      <Box display="flex" gap={3} flexDirection={{xs: 'column', md: 'row'}}>
        <Box flex={1} minWidth={0}>
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            maxZoom={MAP_MAX_ZOOM}
            style={{height: 420, width: '100%'}}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={MAP_MAX_ZOOM}
            />

            <MapViewController
              bounds={bounds}
              fitToken={fitToken}
              onZoomChange={setZoom}
            />

            {bounds && imageUrl && (
              <OverlayEditor
                bounds={bounds}
                imageUrl={imageUrl}
                opacity={opacity}
                onChange={applyBounds}
              />
            )}
          </MapContainer>

          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Overlay opacity
            </Typography>
            <Slider
              value={opacity}
              min={0}
              max={1}
              step={0.05}
              size="small"
              valueLabelDisplay="auto"
              disabled={!bounds || !imageUrl}
              onChange={(__, value) => setOpacity(value as number)}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <Typography variant="body2">Shift</Typography>
            <IconButton
              size="small"
              aria-label="Shift north"
              disabled={!bounds}
              onClick={() => shift('north')}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Shift south"
              disabled={!bounds}
              onClick={() => shift('south')}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Shift west"
              disabled={!bounds}
              onClick={() => shift('west')}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Shift east"
              disabled={!bounds}
              onClick={() => shift('east')}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          flex={1}
          minWidth={0}
          display="flex"
          flexDirection="column"
          gap={2}
        >
          <TextField
            value={formik.values.name ?? ''}
            fullWidth
            name="name"
            label="Name"
            size="small"
            disabled={isBusy}
            error={!!formik.touched.name && !!formik.errors.name}
            helperText={formik.touched.name && formik.errors.name}
            onChange={(event) => {
              formik.setFieldValue('name', event.target.value || null);
              formik.setFieldValue(
                'path',
                kebabCase(event.target.value) || null
              );
            }}
          />

          <TextField
            value={formik.values.path ?? ''}
            fullWidth
            name="path"
            label="Path"
            size="small"
            disabled
            error={!!formik.touched.path && !!formik.errors.path}
            helperText={formik.touched.path && formik.errors.path}
          />

          {coordinateField('north', 'North')}
          {coordinateField('east', 'East')}
          {coordinateField('south', 'South')}
          {coordinateField('west', 'West')}

          <Box position="relative" border="1px solid" borderColor="grey.600">
            <Box
              p={2}
              textAlign="center"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box>
                <CloudUploadIcon fontSize="large" />
                <Box>
                  {formik.values.file
                    ? formik.values.file.name
                    : 'Choose map image to upload'}
                </Box>
              </Box>

              {!formik.values.file && (
                <TextField
                  type="file"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                  }}
                  InputProps={{
                    inputProps: {sx: {height: '100%', cursor: 'pointer'}},
                    sx: {height: '100%'},
                  }}
                  onChange={(event: any) => selectFile(event.target.files[0])}
                />
              )}
            </Box>
          </Box>

          {!!formik.touched.file && !!formik.errors.file && (
            <Typography variant="body2" color="error">
              {formik.errors.file}
            </Typography>
          )}

          {!!serverPreview.error && (
            <Typography variant="body2" color="error">
              {serverPreview.error}
            </Typography>
          )}

          {!!formik.values.file && !isBusy && (
            <Button onClick={clearFile}>Clear</Button>
          )}

          {sourceUpload.isSubmitting && (
            <Box>
              <Box mb={1}>Uploading {sourceUpload.progress}%</Box>
              <LinearProgress
                variant="determinate"
                value={sourceUpload.progress}
              />
            </Box>
          )}

          {serverPreview.isPreparing && !sourceUpload.isSubmitting && (
            <Box>
              <Box mb={1}>Preparing preview…</Box>
              <LinearProgress />
            </Box>
          )}

          {upload.isSubmitting && (
            <Box>
              <Box mb={1}>{upload.progress}%</Box>
              <LinearProgress variant="determinate" value={upload.progress} />
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button onClick={() => onCancel?.()}>Cancel</Button>

        <LoadingButton
          sx={{ml: 1}}
          variant="contained"
          loading={isBusy}
          disabled={serverPreview.isPreparing}
          type="submit"
        >
          Upload
        </LoadingButton>
      </Box>
    </Box>
  );
};
